import { LeadFormFields } from "./LeadFormFields";
import { useSite } from "@/context/SiteContext";
import { resolveBrochureUrl } from "@/lib/brochure";

export const LeadForm = ({ categories = [] }) => {
  const { settings } = useSite();
  const brochure = resolveBrochureUrl(settings?.brochure_url);
  const email = settings?.contact_email || "hello@bondstein.com";

  const bullets = settings?.lead_form_bullets?.length
    ? settings.lead_form_bullets
    : [
        "Discovery + camera audit",
        "Pilot model deployed in 2 weeks",
        "On-prem or hybrid - your call",
      ];

  return (
    <section
      id="demo"
      data-testid="lead-form-section"
      className="relative py-20 sm:py-28 border-t border-neutral-200 bg-[#0A0A0A] text-white overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full blur-3xl opacity-40"
        style={{ background: "radial-gradient(circle, rgba(132,204,22,0.45), transparent 60%)" }}
      />
      <div
        aria-hidden
        className="absolute -bottom-32 -right-24 w-[440px] h-[440px] rounded-full blur-3xl opacity-30"
        style={{ background: "radial-gradient(circle, rgba(245,158,11,0.5), transparent 60%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <span className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-[#84CC16]">
            {settings?.lead_form_eyebrow || "Talk to Bondstein"}
          </span>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight">
            {settings?.lead_form_title_lead || "See Smarter Spaces running on"}{" "}
            <span className="text-[#84CC16]">
              {settings?.lead_form_title_highlight || "your camera feed."}
            </span>
          </h2>
          <p className="mt-5 text-neutral-300 max-w-md leading-relaxed">
            {settings?.lead_form_subtitle ||
              "Share a few details. We'll set up a 30-minute walkthrough - with a live model running on one of your reference camera locations."}
          </p>

          <ul className="mt-8 space-y-3 text-sm">
            {bullets.map((p) => (
              <li key={p} className="flex items-center gap-3 text-neutral-200">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#84CC16] text-[#0A0A0A]">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                {p}
              </li>
            ))}
          </ul>

          <div className="mt-10 pt-6 border-t border-white/10 flex flex-wrap items-center gap-5">
            <a
                href={brochure}
                target="_blank"
                rel="noopener noreferrer"
                download
                data-testid="form-brochure-link"
                className="inline-flex items-center text-sm font-semibold text-[#F59E0B] hover:text-amber-300"
              >
                <svg className="mr-2" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {settings?.cta_brochure_label || "Download brochure"} (PDF)
              </a>
            <a
              href={`mailto:${email}`}
              data-testid="form-mailto-link"
              className="text-sm text-neutral-300 hover:text-white"
            >
              {email}
            </a>
          </div>
        </div>

        <div className="lg:col-span-7 bg-white text-neutral-900 rounded-2xl p-6 sm:p-8 shadow-2xl">
          <LeadFormFields categories={categories} />
        </div>
      </div>
    </section>
  );
};
